import { settings } from '../constants/settings.js';
import { AggregatedQuoteError } from '../sdk/aggregatedQuote/errors/aggregatedQuoteError.js';
import { AmountMismatchError } from '../sdk/aggregatedQuote/errors/amountMismatchError.js';
import { getAggregatedQuoteExactIn } from '../sdk/aggregatedQuote/getAggregatedQuoteExactIn.js';
import { quoteWithLog } from '../sdk/solverRelay/utils/quoteWithLog.js';

function isFailedQuote(quote) {
    return "type" in quote;
}
async function queryQuote(input, { signal, } = {}) {
    try {
        const aggregateQuote = await getAggregatedQuoteExactIn({
            aggregatedQuoteParams: {
                tokensIn: input.tokensIn,
                tokenOut: input.tokenOut,
                amountIn: input.amountIn,
                balances: input.balances,
                waitMs: input.waitMs,
            },
            config: {
                fetchOptions: { signal },
            },
        });
        return {
            tag: "ok",
            value: {
                quoteHashes: aggregateQuote.quoteHashes,
                expirationTime: aggregateQuote.expirationTime,
                tokenDeltas: aggregateQuote.tokenDeltas,
            },
        };
    }
    catch (err) {
        if (err instanceof AggregatedQuoteError) {
            const quoteError = err.errors.find((e) => e.quote != null);
            if (quoteError?.quote) {
                return {
                    tag: "err",
                    value: {
                        reason: `ERR_${quoteError.quote.type}`,
                    },
                };
            }
            return {
                tag: "err",
                value: {
                    reason: "ERR_NO_QUOTES",
                },
            };
        }
        if (err instanceof AmountMismatchError) {
            return {
                tag: "err",
                value: {
                    reason: "ERR_UNFULFILLABLE_AMOUNT",
                    shortfall: err.shortfall,
                    overage: err.overage,
                },
            };
        }
        throw err;
    }
}
async function queryQuoteExactOut(input, { logBalanceSufficient, signal, }) {
    const quotes = await quoteWithLog({
        defuse_asset_identifier_in: input.tokenIn,
        defuse_asset_identifier_out: input.tokenOut,
        exact_amount_out: input.exactAmountOut.toString(),
        min_deadline_ms: input.minDeadlineMs ?? settings.quoteMinDeadlineMs,
    }, {
        fetchOptions: { signal },
        logBalanceSufficient: logBalanceSufficient,
    });
    if (quotes == null) {
        return {
            tag: "err",
            value: {
                reason: "ERR_NO_QUOTES",
            },
        };
    }
    const failedQuotes = [];
    const validQuotes = [];
    for (const q of quotes) {
        if (isFailedQuote(q)) {
            failedQuotes.push(q);
        }
        else {
            validQuotes.push(q);
        }
    }
    validQuotes.sort((a, b) => {
        // Sort by `amount_in` in ascending order, because backend does not sort
        if (BigInt(a.amount_in) < BigInt(b.amount_in))
            return -1;
        if (BigInt(a.amount_in) > BigInt(b.amount_in))
            return 1;
        return 0;
    });
    const bestQuote = validQuotes[0];
    if (bestQuote) {
        return {
            tag: "ok",
            value: {
                quoteHashes: [bestQuote.quote_hash],
                expirationTime: bestQuote.expiration_time,
                tokenDeltas: [
                    [input.tokenIn, -BigInt(bestQuote.amount_in)],
                    [input.tokenOut, BigInt(bestQuote.amount_out)],
                ],
            },
        };
    }
    if (failedQuotes[0]) {
        return {
            tag: "err",
            value: {
                reason: `ERR_${failedQuotes[0].type}`,
            },
        };
    }
    return {
        tag: "err",
        value: {
            reason: "ERR_NO_QUOTES",
        },
    };
}

export { isFailedQuote, queryQuote, queryQuoteExactOut };
//# sourceMappingURL=quoteService.js.map
